import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Building2, KeyRound, FileSearch, ClipboardCheck, History } from 'lucide-react';
import { UpgradeGate } from '../components/org-security/shared';
import { EntraIdAssessment } from '../components/org-security/EntraIdAssessment';
import { DesignReview } from '../components/org-security/DesignReview';
import { SecurityAssurance } from '../components/org-security/SecurityAssurance';
import { AssessmentHistory } from '../components/org-security/AssessmentHistory';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOCKED_PLANS = ['trial'];

export default function OrgSecurityPage() {
  const { user, checkAuth } = useAuth();
  const [history, setHistory] = useState([]);
  const [costs, setCosts] = useState({});
  const planId = user?.credits?.plan_id || user?.subscription?.plan_id || 'trial';
  const locked = LOCKED_PLANS.includes(planId);

  const fetchHistory = useCallback(async () => { try { const r = await axios.get(`${API}/org-security/assessments`, { withCredentials: true }); setHistory(r.data); } catch {} }, []);
  useEffect(() => { fetchHistory(); axios.get(`${API}/credits/info`, { withCredentials: true }).then(r => setCosts(r.data.costs || {})).catch(() => {}); }, [fetchHistory]);

  const onComplete = () => { fetchHistory(); checkAuth(); };
  const deleteAssessment = async (id) => { try { await axios.delete(`${API}/org-security/assessments/${id}`, { withCredentials: true }); setHistory(h => h.filter(a => a.assessment_id !== id)); toast.success('Assessment deleted'); } catch { toast.error('Delete failed'); } };

  return (
    <div className="space-y-6" data-testid="org-security-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-heading tracking-tight flex items-center gap-3" data-testid="org-security-title"><Building2 className="h-7 w-7 text-primary" /> Organizational Security</h1>
          <p className="text-sm text-muted-foreground mt-1">Enterprise-level security controls: identity posture, architecture reviews and control assurance</p>
        </div>
        <Badge variant="outline" className={locked ? 'border-amber-500/30 text-amber-500' : 'border-green-500/30 text-green-500'} data-testid="org-plan-badge">{user?.credits?.plan_label || planId} plan</Badge>
      </div>

      {locked && <UpgradeGate />}

      <Tabs defaultValue="entra" className="w-full">
        <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 h-auto" data-testid="org-tabs">
          <TabsTrigger value="entra" className="gap-1.5 py-2" data-testid="tab-entra"><KeyRound className="h-4 w-4" /> Entra ID</TabsTrigger>
          <TabsTrigger value="design" className="gap-1.5 py-2" data-testid="tab-design"><FileSearch className="h-4 w-4" /> HLD / LLD Review</TabsTrigger>
          <TabsTrigger value="assurance" className="gap-1.5 py-2" data-testid="tab-assurance"><ClipboardCheck className="h-4 w-4" /> Security Assurance</TabsTrigger>
          <TabsTrigger value="history" className="gap-1.5 py-2" data-testid="tab-history"><History className="h-4 w-4" /> History ({history.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="entra" className="mt-6"><EntraIdAssessment locked={locked} cost={costs.entra_assessment ?? 15} onComplete={onComplete} /></TabsContent>
        <TabsContent value="design" className="mt-6"><DesignReview locked={locked} cost={costs.design_review ?? 20} onComplete={onComplete} /></TabsContent>
        <TabsContent value="assurance" className="mt-6"><SecurityAssurance locked={locked} cost={costs.assurance_assessment ?? 10} onComplete={onComplete} /></TabsContent>
        <TabsContent value="history" className="mt-6"><AssessmentHistory items={history} onDelete={deleteAssessment} /></TabsContent>
      </Tabs>
    </div>
  );
}
